import {
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Alert,
  Link,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkAssessmentResultAcknowledged,
  resetUkAssessment,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import UKAssessmentWaitPending from "./UKAssessmentWaitPending";

const COOLING_OFF_FOOTER =
  "A 24 hour cooling off period will now apply until you are able to trade crypto. This is to ensure you take the time to consider if the product is right for you.";

const COOLING_OFF_FOOTER_WARNING =
  "A 24 hour cooling off period will apply until you are able to trade crypto. This is to ensure you take the time to consider if the product is right for you.";

const SUPPORT_ARTICLE_URL =
  "https://calebandbrown.com/en-gb/uk-cryptoasset-investing";

export default function UKAssessmentResult() {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();
  const { assessment } = ukOnboarding;

  const isPassed = assessment.status === "passed";
  const isLockedOut = assessment.lockoutEndsAt !== null && assessment.lockoutEndsAt > Date.now();
  const hasWaitPeriod =
    assessment.nextAttemptAvailableAt !== null && assessment.nextAttemptAvailableAt > Date.now();
  const canRetry =
    !isPassed && !isLockedOut && assessment.attemptNumber < assessment.maxAttempts;

  // "182 days (26 January 2027)" — how long the lockout has left plus the
  // exact date it ends
  const formatLockoutRemaining = (timestamp: number | null) => {
    if (!timestamp) return "";
    const days = Math.max(1, Math.ceil((timestamp - Date.now()) / (24 * 60 * 60 * 1000)));
    const date = new Date(timestamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return `${days} day${days === 1 ? "" : "s"} (${date})`;
  };

  const handleProceedToVerification = () => {
    dispatch(setUkAssessmentResultAcknowledged(true));
    // Privacy notice & consent must be acknowledged before any Onfido page
    dispatch(setApplicationStep("onfidoConsent"));
  };

  const handleReject = () => {
    window.location.href = "/dashboard";
  };

  const handleRetry = () => {
    dispatch(resetUkAssessment());
    dispatch(setApplicationStep("ukAssessment"));
  };

  const handleExit = () => {
    window.location.href = "/";
  };

  // "Warning and manual review" (score 80-94): a provisional pass. The
  // cooling-off period has already started and the client continues to
  // verification — support reviews the borderline score in the background.
  if (isPassed && assessment.manualReview) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You have passed the assessment
            </Typography>
            <Stack spacing={2} mt={2}>
              <Typography variant="body1">
                Based on your test results, our support team will review your responses and may
                contact you at{" "}
                <Link href="mailto:support@calebandbrown.com" fontWeight={700}>
                  support@calebandbrown.com
                </Link>{" "}
                if we need further information. You can continue with your identity verification
                in the meantime.
              </Typography>
              <Typography variant="body1" fontWeight={700} fontStyle="italic">
                Don't invest unless you're prepared to lose all the money you invest. This is a
                high-risk investment and you should not expect to be protected if something goes
                wrong.
              </Typography>
              <Typography variant="body1">
                By indicating that you wish to proceed, you acknowledge the warning given and
                understand the risks involved in investing in cryptoassets.
              </Typography>
              <Typography variant="body2" fontStyle="italic" color="text.secondary">
                {COOLING_OFF_FOOTER}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            mt={2}
          >
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleReject}
              sx={{ minWidth: 200 }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleProceedToVerification}
              sx={{ minWidth: 200 }}
            >
              Accept
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (isPassed && assessment.passedWithWarning) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You have passed the assessment
            </Typography>
            <Stack spacing={2} mt={2}>
              <Typography variant="body1">
                Based on the information you have provided, you have shown a good understanding
                of the risks involved in trading cryptoassets. However, we still recommend that
                you carefully consider whether our products and services are right for your
                individual circumstances & financial situation and whether you can afford to lose
                all of your deposits. If you wish to proceed, we encourage you to use the
                education materials we make available on our website.
              </Typography>
              <Typography variant="body1" fontWeight={700} fontStyle="italic">
                Don't invest unless you're prepared to lose all the money you invest. This is a
                high-risk investment and you should not expect to be protected if something goes
                wrong.
              </Typography>
              <Typography variant="body1">
                By indicating that you wish to proceed, you acknowledge the warning given and
                understand the risks involved in investing in cryptoassets.
              </Typography>
              <Typography variant="body2" fontStyle="italic" color="text.secondary">
                {COOLING_OFF_FOOTER_WARNING}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            mt={2}
          >
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleReject}
              sx={{ minWidth: 200 }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleProceedToVerification}
              sx={{ minWidth: 200 }}
            >
              Accept
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (isPassed) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#E8F5E9",
              border: "2px solid #4CAF50",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "#4CAF50", mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You have passed the assessment
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ mt: 2 }}>
              You have shown a good understanding of the risks involved in trading cryptoassets.
            </Typography>
            <Typography variant="body2" fontStyle="italic" color="text.secondary">
              {COOLING_OFF_FOOTER}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleProceedToVerification}
              sx={{ minWidth: 200 }}
            >
              OK
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  // Second fail: the 24-hour wait before the final attempt gets the same
  // countdown interface as the post-pass cooling-off period
  if (hasWaitPeriod && !isLockedOut) {
    return <UKAssessmentWaitPending />;
  }

  // Failed scenarios
  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: "center",
            backgroundColor: isLockedOut ? "#FFEBEE" : "#FFF3E0",
            border: isLockedOut ? "2px solid #F44336" : "2px solid #FF9800",
          }}
        >
          {isLockedOut ? (
            <>
              <BlockIcon sx={{ fontSize: 80, color: "#F44336", mb: 2 }} />
              <Typography variant="h4" fontWeight={600} gutterBottom color="#C62828">
                Assessment Locked
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                You have exceeded the maximum number of attempts allowed.
              </Typography>
              <Alert severity="error" sx={{ mt: 2, textAlign: "left" }}>
                You can re-apply after:{" "}
                <strong>{formatLockoutRemaining(assessment.lockoutEndsAt)}</strong>
              </Alert>
            </>
          ) : (
            <>
              <CancelOutlinedIcon sx={{ fontSize: 80, color: "#FF9800", mb: 2 }} />
              <Typography variant="h4" fontWeight={600} gutterBottom color="#E65100">
                Assessment Not Passed
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Unfortunately, you did not pass the appropriateness assessment this time.
              </Typography>
              {assessment.knockoutFailed && (
                <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
                  <strong>Important:</strong> You answered a critical question incorrectly.
                  These questions test fundamental understanding of cryptoasset risks.
                </Alert>
              )}
            </>
          )}
        </Paper>
      </Grid>

      {!isLockedOut && (
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {canRetry ? "What Can You Do?" : "Next Steps"}
            </Typography>

            {canRetry ? (
              <Stack spacing={2}>
                <Typography variant="body1">
                  You have <strong>{assessment.maxAttempts - assessment.attemptNumber}</strong>{" "}
                  attempt(s) remaining.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "14px" }}>
                  We recommend reviewing this{" "}
                  <Link
                    href={SUPPORT_ARTICLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontWeight={700}
                  >
                    support article
                  </Link>{" "}
                  to learn more about the cryptocurrency asset class, the UK regulatory
                  environment and Caleb &amp; Brown before attempting the test again.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Typography variant="body1">
                  Unfortunately, you have used all available attempts.
                </Typography>
                <Typography variant="body1">
                  If you believe cryptoasset investment is right for you, you may re-apply
                  after the lockout period expires.
                </Typography>
                <Typography variant="body1">
                  In the meantime, we encourage you to learn more about cryptoassets and the
                  associated risks.
                </Typography>
              </Stack>
            )}
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Why was I unsuccessful?</strong>
            <br />
            The appropriateness assessment ensures that investors understand the risks
            associated with cryptoasset investments. This protects both you and us by ensuring
            you are making an informed decision about your investments.
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          mt={2}
        >
          <Button variant="outlined" color="inherit" size="large" onClick={handleExit}>
            Exit Application
          </Button>
          {canRetry && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleRetry}
              sx={{ minWidth: 200 }}
            >
              Retry Assessment
            </Button>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
}
